import { useState } from "react";
import { Send, Loader2, Paperclip, X, MessageSquare, Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface TicketInteractionFormProps {
  ticketId: string;
  ticketStatus: string;
  userType: 'client' | 'internal';
  onSubmit: (data: InteractionData) => Promise<void>;
  isSubmitting?: boolean;
}

export interface InteractionData {
  type: 'comment' | 'internal_note' | 'solution' | 'info_request' | 'validation';
  message: string;
  metadata?: {
    time_spent?: number;
    resolved?: boolean;
  };
  attachments?: File[];
}

const INTERACTION_TYPES = {
  comment: {
    label: 'Comentário',
    icon: MessageSquare,
    description: 'Adicionar um comentário público',
    color: 'text-gray-600'
  },
  internal_note: {
    label: 'Nota Interna',
    icon: Lock,
    description: 'Nota visível apenas para a equipe',
    color: 'text-amber-600'
  },
  solution: {
    label: 'Propor Solução',
    icon: CheckCircle2,
    description: 'Enviar solução para o cliente validar',
    color: 'text-green-600'
  },
  info_request: {
    label: 'Pedir Informações',
    icon: AlertCircle,
    description: 'Solicitar mais detalhes do cliente',
    color: 'text-orange-600'
  }
};

export function TicketInteractionForm({
  ticketId,
  ticketStatus,
  userType,
  onSubmit,
  isSubmitting = false
}: TicketInteractionFormProps) {
  const [interactionType, setInteractionType] = useState<string>('comment');
  const [message, setMessage] = useState('');
  const [timeSpent, setTimeSpent] = useState<number>(0);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Tipos disponíveis baseados no tipo de usuário
  const availableTypes = userType === 'internal' 
    ? ['comment', 'internal_note', 'solution', 'info_request']
    : ['comment'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!message.trim()) {
      setError('A mensagem não pode estar vazia');
      return;
    }

    try {
      const data: InteractionData = {
        type: interactionType as any,
        message: message.trim(),
        metadata: {},
        attachments
      };

      // Adicionar tempo gasto se fornecido
      if (timeSpent > 0) {
        data.metadata!.time_spent = timeSpent;
      }

      await onSubmit(data);

      // Limpar formulário após sucesso
      setMessage('');
      setTimeSpent(0);
      setAttachments([]);
      setInteractionType('comment');
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar interação');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments(prev => [...prev, ...newFiles]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Não mostrar formulário se ticket estiver fechado
  if (ticketStatus === 'fechado' || ticketStatus === 'closed') {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            <Lock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Este ticket está fechado. Não é possível adicionar novas interações.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Adicionar Interação</CardTitle>
        <CardDescription>
          {userType === 'internal' 
            ? 'Escolha o tipo de interação e adicione sua mensagem'
            : 'Adicione um comentário ou responda à equipe'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tipo de Interação - Apenas para usuários internos */}
          {userType === 'internal' && (
            <div className="space-y-2">
              <Label>Tipo de Interação</Label>
              <Select value={interactionType} onValueChange={setInteractionType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableTypes.map(type => {
                    const config = INTERACTION_TYPES[type as keyof typeof INTERACTION_TYPES];
                    const Icon = config.icon;
                    return (
                      <SelectItem key={type} value={type}>
                        <div className="flex items-center gap-2">
                          <Icon className={`h-4 w-4 ${config.color}`} />
                          <span>{config.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {interactionType && (
                <p className="text-xs text-muted-foreground">
                  {INTERACTION_TYPES[interactionType as keyof typeof INTERACTION_TYPES]?.description}
                </p>
              )}
            </div>
          )}

          {/* Mensagem */}
          <div className="space-y-2">
            <Label htmlFor="message">Mensagem *</Label>
            <Textarea
              id="message"
              placeholder={
                interactionType === 'solution' 
                  ? 'Descreva a solução aplicada...'
                  : interactionType === 'info_request'
                  ? 'Que informações você precisa?'
                  : 'Digite sua mensagem...'
              }
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              disabled={isSubmitting}
              className="resize-none"
            />
          </div>

          {/* Tempo Gasto - Apenas para soluções */}
          {userType === 'internal' && interactionType === 'solution' && (
            <div className="space-y-2">
              <Label htmlFor="time-spent">Tempo Gasto (minutos)</Label>
              <input
                id="time-spent"
                type="number"
                min="0"
                value={timeSpent}
                onChange={(e) => setTimeSpent(parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                placeholder="0"
              />
            </div>
          )}

          {/* Anexos */}
          <div className="space-y-2">
            <Label>Anexos</Label>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('file-input')?.click()}
                disabled={isSubmitting}
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Adicionar Arquivo
              </Button>
              <input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.zip"
              />
            </div>

            {/* Lista de Anexos */}
            {attachments.length > 0 && (
              <div className="space-y-2 mt-2">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 border rounded-lg bg-muted/50"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAttachment(index)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Erro */}
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Botão de Envio */}
          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={isSubmitting || !message.trim()}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {interactionType === 'solution' 
                    ? 'Enviar Solução'
                    : interactionType === 'info_request'
                    ? 'Solicitar Informações'
                    : 'Enviar'}
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
